/**
 * Client-side authentication utilities for BuildBright contractors
 */

const isDev =
  window.location.hostname === "localhost" ||
  window.location.hostname.includes("github");

const API_ORIGIN = isDev ? "" : "https://api.buildbright.app";

// dev: CRA proxy -> /api/contractors/*
// prod: Cloudflare Worker -> /contractor/proxy/*
const CONTRACTOR_API = `${API_ORIGIN}/api/contractor`;

/**
 * Check if user is authenticated by calling the /me endpoint
 */
export const checkAuth = async () => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/me`, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.contractor;
    }
    return null;
  } catch (error) {
    console.error("Auth check failed:", error);
    return null;
  }
};

/**
 * Sign up a new contractor
 */
export const signUp = async (userData) => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/signup`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    const data = await response.json();

    if (!response.ok) {
      const message =
        typeof data.detail === "string"
          ? data.detail
          : data.detail?.message ||
            data.detail?.code ||
            data.message ||
            "Sign up failed";

      throw new Error(message);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign in contractor
 */
export const signIn = async (email, password) => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/signin`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message =
        typeof data.detail === "string"
          ? data.detail
          : data.detail?.message ||
            data.detail?.code ||
            data.message ||
            "Sign in failed";

      throw new Error(message);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Sign out contractor
 */
export const signOut = async () => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/signout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Sign out failed:", error);
    return false;
  }
};

/**
 * Request password reset
 */
export const forgotPassword = async (email) => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/forgot`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Password reset request failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Reset password with token
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/reset`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Password reset failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify email with token
 */
export const verifyEmail = async (token) => {
  try {
    const response = await fetch(
      `${CONTRACTOR_API}/verify?token=${encodeURIComponent(token)}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Email verification failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update contractor profile
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await fetch(`${CONTRACTOR_API}/profile`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(profileData),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Profile update failed");
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 */
export const isValidPassword = (password) => {
  return password && password.length >= 8;
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phoneNumber;
};
